import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { format } from 'date-fns'

const baseURL = process.env.REACT_APP_BASE_URL
const associatesURL = `${baseURL}/api/associate/get-list?isActive=true`
const createAssociateURL = `${baseURL}/api/associate/create`
const updateAssociateURL = `${baseURL}/api/associate/update`
const deleteAssociateURL = (associateId) =>
  `${baseURL}/api/associate/delete?associateId=${associateId}`
const getAssociateURL = (associateId) =>
  `${baseURL}/api/associate/get-associate?associateId=${associateId}`
const associatePasswordResetURL = (associateId, phoneNumber) =>
  `${baseURL}/api/associate/adminresetpassword?associateId=${associateId}&phoneNumber=${phoneNumber}`
const entityExistCheckURL = (entity, value, userid) =>
  `${baseURL}/api/associate/validate-entity?entity=${entity}&value=${value}&userid=${userid}`

export const fetchAssociates = createAsyncThunk(
  'associates/fetchAssociates',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(associatesURL, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const createAssociate = createAsyncThunk(
  'associates/createAssociateURL',
  async (associate, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const associateModel = {
        associateBasicDetailsModel: {
          id: 0,
          bidid: '',
          designationId: associate.designationid,
          name: associate.name,
          fatherHusbandName: associate.fatherHusbandName,
          dateOfBirth: associate.dateOfBirth
            ? format(new Date(associate.dateOfBirth), 'yyyy-MM-dd')
            : '1900-01-01',
          occupation: associate.occupation,
          weddingAnniversary: associate.weddingAnniversary
            ? format(new Date(associate.weddingAnniversary), 'yyyy-MM-dd')
            : '1900-01-01',
          sponsoredBy: associate.sponsoredById,
          isActive: true,
          dateOfJoin: associate.dateOfJoin
            ? format(new Date(associate.dateOfJoin), 'yyyy-MM-dd')
            : '1900-01-01',
          designationCodeName: '',
          sponsoredByIDName: '',
          bankAccountNumber: associate.bankaccountnumber,
        },
        associateContactDetailsModel: {
          id: 0,
          associateId: 0,
          email: associate.email,
          phoneNumber1: associate.phone1,
          phoneNumber2: associate.phone2,
          address: associate.address,
          isActive: true,
        },
        associateIdentityDetailsModel: {
          id: 0,
          associateId: 0,
          panNumber: associate.pan,
          aadhaarNumber: associate.aadhar,
          resienditalProofPath: associate.residentialProof
            ? associate.residentialProof.base64
            : '',
          panCardPath: associate.panCard ? associate.panCard.base64 : '',
          aadhaarCardPath: associate.aadharCard
            ? associate.aadharCard.base64
            : '',
          isActive: true,
        },
      }

      const response = await axios.post(
        createAssociateURL,
        JSON.stringify(associateModel),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (response.data.isSuccess) {
        dispatch(fetchAssociates())
      } else {
        console.error(
          'Error: The backend did not indicate success:',
          response.data
        )
      }
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const associatePasswordReset = createAsyncThunk(
  'associates/updateAssociateURL',
  async (passwordRestModel, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      if (!accessToken) {
        throw new Error('Authentication token is missing. Please log in again.')
      }

      const response = await axios.put(
        associatePasswordResetURL(passwordRestModel.associateId, passwordRestModel.phoneNumber),
        null,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (response.data.isSuccess) {
        dispatch(fetchAssociates())
        return response.data
      } else {
        console.error('API did not indicate success:', response.data)
        return rejectWithValue('Password reset failed. Please try again.')
      }
    } catch (error) {
      console.error('Error in associatePasswordReset thunk:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        'An error occurred while resetting the password.'
      return rejectWithValue(errorMessage)
    }
  }
)

export const updateAssociate = createAsyncThunk(
  'associates/updateAssociateURL',
  async (associate, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const associateModel = {
        associateBasicDetailsModel: {
          id: associate.id,
          bidid: '',
          designationId: associate.designationid,
          name: associate.name,
          fatherHusbandName: associate.fatherHusbandName,
          dateOfBirth: associate.dateOfBirth
            ? format(new Date(associate.dateOfBirth), 'yyyy-MM-dd')
            : '1900-01-01',
          occupation: associate.occupation,
          weddingAnniversary: associate.weddingAnniversary
            ? format(new Date(associate.weddingAnniversary), 'yyyy-MM-dd')
            : '1900-01-01',
          sponsoredBy: associate.sponsoredById,
          isActive: true,
          dateOfJoin: associate.dateOfJoin
            ? format(new Date(associate.dateOfJoin), 'yyyy-MM-dd')
            : '1900-01-01',
          designationCodeName: '',
          sponsoredByIDName: '',
          bankAccountNumber: associate.bankaccountnumber,
        },
        associateContactDetailsModel: {
          id: associate.assContactId,
          associateId: associate.id,
          email: associate.email,
          phoneNumber1: associate.phone1,
          phoneNumber2: associate.phone2,
          address: associate.address,
          isActive: true,
        },
        associateIdentityDetailsModel: {
          id: associate.assIdentityId,
          associateId: associate.id,
          panNumber: associate.pan,
          aadhaarNumber: associate.aadhar,
          resienditalProofPath: associate.residentialProof
            ? associate.residentialProof
            : '',
          panCardPath: associate.panCard ? associate.panCard : '',
          aadhaarCardPath: associate.aadharCard
            ? associate.aadhaarCardPath
            : '',
          isActive: true,
        },
      }

      const response = await axios.put(
        updateAssociateURL,
        JSON.stringify(associateModel),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (response.data.isSuccess) {
        dispatch(fetchAssociates())
      } else {
        console.error(
          'Error: The backend did not indicate success:',
          response.data
        )
      }
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const deleteAssociate = createAsyncThunk(
  'associates/deleteAssociate',
  async (associateId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(deleteAssociateURL(associateId), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data)
      }

      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const getAssociate = createAsyncThunk(
  'associates/getAssociate',
  async (associateId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(getAssociateURL(associateId), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data)
      }
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const checkEntityExist = createAsyncThunk(
  'associates/checkEntityExist',
  async (inputs, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(entityExistCheckURL(inputs.entity, inputs.value, inputs.userid), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data)
      }
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

const associateApiSlice = createSlice({
  name: 'associates',
  initialState: {
    associates: [],
    associateDetails: null,
    status: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssociates.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchAssociates.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.associates = action.payload.data || []
      })
      .addCase(fetchAssociates.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(getAssociate.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getAssociate.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.associateDetails = action.payload.data
      })
      .addCase(getAssociate.rejected, (state, action) => {
        state.status = 'failed'
        state.error =
          action.payload || 'An error occurred while fetching associate details'
      })
      .addCase(checkEntityExist.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(checkEntityExist.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.associateDetails = action.payload.data
      })
      .addCase(checkEntityExist.rejected, (state, action) => {
        state.status = 'failed'
        state.error =
          action.payload || 'An error occurred while fetching associate details'
      })
      .addCase('RESET', () => ({
        associates: [],
        status: 'idle',
        error: null,
      }))
  },
})

export default associateApiSlice.reducer
